import { MarketsData } from '@silo/types';
import { useSiloWeb3 } from '@silo/web3';
import { getIsLiquidationWarningEnabled } from 'app/functions/feature-flags/feature-flags';
import { LiquidationBanner } from '../nav/liquidation-banner';
import { PATH } from 'app/hooks/useRoute';
import { Markets, useBreakpointValue } from '@silo/ui';
import { TvlDisplay } from './tvl-display';
import { UI_DEPLOYMENT_DATE } from 'app/constants';
import { useCallback, useEffect, useState } from 'react';
import { transformMarkets } from '@silo/utils';
import { MarketsList } from './markets-list';

interface MarketsV2Props {
  marketsData: MarketsData;
}

const { useMarketsSortState } = Markets;

export function MarketsV2({ marketsData }: MarketsV2Props): JSX.Element {
  const { chainId, protocolKey } = useSiloWeb3();

  const isLiquidationWarningOn = getIsLiquidationWarningEnabled(chainId);

  const [search, setSearch] = useState<string | null>(null);
  const { marketsSort, sortBy, resetSort } = useMarketsSortState();
  const [pages, setPages] = useState(1);

  const { marketsByProtocol, revenueApr } = marketsData;

  const { markets, allMarkets, hasMorePages } = transformMarkets({
    marketsByProtocol,
    marketsOptions: {
      protocolKey,
      sort: marketsSort,
      search,
      pages,
    },
  });

  const loadMore = useCallback(async (): Promise<void> => {
    if (hasMorePages) {
      setPages((pages) => pages + 1);
    }
  }, [hasMorePages]);

  const showCards = useBreakpointValue(true, { md: false });

  useEffect(() => {
    setPages(1);
  }, [search, showCards]);

  return (
    <div className="flex flex-col gap-10">
      {isLiquidationWarningOn ? <LiquidationBanner href={PATH.DASHBOARD} /> : null}

      <Markets.Layout
        tvlDisplay={<TvlDisplay marketsData={marketsByProtocol} />}
        trustSignals={<Markets.TrustSignals deploymentDate={UI_DEPLOYMENT_DATE} />}
        heading={<Markets.Heading protocolKey={protocolKey} />}
        toolbar={
          <Markets.Toolbar
            onPatternChange={setSearch}
            sortMenu={
              <Markets.SortMenu
                protocolKey={protocolKey}
                marketsSort={marketsSort}
                sortBy={sortBy}
                resetSort={resetSort}
              />
            }
          />
        }
        markets={
          <MarketsList
            // resetting on showCards change ensures that infinite scroll works
            key={`${showCards}`}
            allMarkets={allMarkets}
            markets={markets}
            loadMore={loadMore}
            showCards={showCards}
            hasMorePages={hasMorePages}
            marketsSort={marketsSort}
            sortBy={sortBy}
            revenueApr={revenueApr}
          />
        }
      />
    </div>
  );
}
